import React, { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, X, Pencil, Trash2, ArrowUp, ArrowDown, ExternalLink, Sparkles, Image as ImageIcon } from 'lucide-react';
import { resolveImageUrl } from '@/lib/img';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { Link } from 'react-router-dom';

const CATEGORY_OPTIONS = [
  { value: 'food', label: 'Food' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'event', label: 'Event' },
];

const emptyImage = { image_url: '', caption: '', category: 'food' };

export default function AdminTastePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // standalone gallery editor
  const [captionEdit, setCaptionEdit] = useState(null); // linked menu-item taste-caption editor

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/taste');
      setRows(data || []);
    } catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const persistOrder = async (newRows) => {
    // Reassign sort_order densely (10, 20, 30 …) so ties can be broken later
    const payload = newRows.map((r, idx) => ({ kind: r.kind, id: r.id, order: (idx + 1) * 10 }));
    setRows(newRows.map((r, idx) => ({ ...r, sort_order: (idx + 1) * 10 })));
    try { await api.post('/admin/taste/reorder', payload); } catch { toast.error('Reorder failed'); await load(); }
  };

  const move = (row, delta) => {
    const idx = rows.findIndex((r) => r.id === row.id && r.kind === row.kind);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[idx], next[target]] = [next[target], next[idx]];
    persistOrder(next);
  };

  const addImage = async () => {
    if (!editing?.image_url) { toast.error('Please choose an image'); return; }
    try {
      if (editing.id) {
        await api.put(`/admin/gallery/${editing.id}`, {
          image_url: editing.image_url,
          caption: editing.caption || null,
          category: editing.category || 'food',
        });
      } else {
        await api.post('/admin/gallery', {
          image_url: editing.image_url,
          caption: editing.caption || null,
          category: editing.category || 'food',
          sort_order: (rows.length + 1) * 10,
        });
      }
      toast.success('Saved');
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const removeStandalone = async (row) => {
    if (!window.confirm('Remove this image from A Taste of This Place?')) return;
    try { await api.delete(`/admin/gallery/${row.id}`); toast.success('Removed'); await load(); }
    catch { toast.error('Remove failed'); }
  };

  const unlinkMenuItem = async (row) => {
    if (!window.confirm(`Remove "${row.menu_item_name}" from A Taste of This Place?\n\nThe dish itself will remain on the Menu.`)) return;
    try {
      await api.put(`/admin/items/${row.menu_item_id}`, { show_in_taste_of_this_place: false });
      toast.success('Removed from taste');
      await load();
    } catch { toast.error('Update failed'); }
  };

  const saveCaption = async () => {
    try {
      await api.put(`/admin/items/${captionEdit.menu_item_id}`, { taste_caption: captionEdit.caption });
      toast.success('Caption updated');
      setCaptionEdit(null);
      await load();
    } catch { toast.error('Update failed'); }
  };

  const stats = useMemo(() => ({
    linked: rows.filter((r) => r.kind === 'menu').length,
    standalone: rows.filter((r) => r.kind === 'gallery').length,
  }), [rows]);

  return (
    <AdminLayout>
      <div data-testid="admin-taste-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-2">
          <div>
            <h1 className="font-display text-3xl text-white">A TASTE OF THIS PLACE</h1>
            <p className="text-sm text-white/65 mt-1">
              Manage the photos shown in the &ldquo;A Taste of This Place&rdquo; section on the homepage.
            </p>
            <p className="text-xs text-white/45 mt-1">
              {stats.linked} linked menu item{stats.linked === 1 ? '' : 's'} · {stats.standalone} standalone image{stats.standalone === 1 ? '' : 's'}
            </p>
          </div>
          <button
            onClick={() => setEditing({ ...emptyImage })}
            data-testid="taste-add-image-button"
            className="h-10 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold inline-flex items-center gap-2"
          >
            <Plus size={16} /> Add image
          </button>
        </div>

        {loading ? (
          <div className="text-white/60 py-8">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 mt-4">
            {rows.map((row) => (
              <div key={`${row.kind}-${row.id}`} className="sb-card overflow-hidden" data-testid={`taste-row-${row.kind}-${row.id}`}>
                <div className="aspect-[4/3] bg-black relative">
                  <img src={resolveImageUrl(row.image_url)} alt={row.caption} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute top-1.5 left-1.5 flex flex-wrap gap-1">
                    {row.kind === 'menu' ? (
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[hsl(var(--sb-gold))] text-black font-semibold inline-flex items-center gap-1">
                        <Sparkles size={9} /> Menu item
                      </span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-black/70 text-white/85 border border-white/20 inline-flex items-center gap-1">
                        <ImageIcon size={9} /> {(row.category || 'food')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-2">
                  <div className="text-xs text-white truncate">
                    {row.caption || <span className="text-white/45">No caption</span>}
                  </div>
                  {row.kind === 'menu' && (
                    <div className="text-[10px] text-white/50 truncate">Dish: {row.menu_item_name}</div>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-white/50">#{row.sort_order}</span>
                    <div className="flex gap-0.5">
                      <button
                        onClick={() => move(row, -1)}
                        title="Move up"
                        className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-white/80"
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        onClick={() => move(row, 1)}
                        title="Move down"
                        className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-white/80"
                      >
                        <ArrowDown size={12} />
                      </button>
                      {row.kind === 'menu' ? (
                        <>
                          <button
                            onClick={() => setCaptionEdit({ menu_item_id: row.menu_item_id, caption: row.caption, menu_item_name: row.menu_item_name })}
                            title="Edit caption"
                            className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-white/80"
                          >
                            <Pencil size={12} />
                          </button>
                          <Link
                            to="/admin/menu"
                            title="Edit Menu Item"
                            className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-white/80"
                          >
                            <ExternalLink size={12} />
                          </Link>
                          <button
                            onClick={() => unlinkMenuItem(row)}
                            title="Remove from A Taste of This Place"
                            className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-[hsl(var(--sb-red))]"
                          >
                            <X size={12} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditing({ ...row })}
                            title="Edit"
                            className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-white/80"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            onClick={() => removeStandalone(row)}
                            title="Delete"
                            className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-[hsl(var(--sb-red))]"
                          >
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {!rows.length && (
              <div className="col-span-full text-center text-white/55 py-10">
                No content yet. Add an image or enable &ldquo;Show in A Taste of This Place&rdquo; on a Menu Item.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Standalone image editor (Type B) */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={() => setEditing(null)}>
          <div
            className="w-full max-w-md bg-[hsl(var(--sb-surface))] h-full overflow-y-auto border-l border-[hsl(var(--sb-border))]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[hsl(var(--sb-border))] flex items-center justify-between">
              <div className="font-display text-xl text-white">{editing.id ? 'EDIT IMAGE' : 'ADD IMAGE'}</div>
              <button onClick={() => setEditing(null)} className="h-9 w-9 grid place-items-center text-white/70"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <ImageUploader label="Image" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} aspect="video" />
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-white/55">Caption</span>
                <input
                  value={editing.caption || ''}
                  onChange={(e) => setEditing({ ...editing, caption: e.target.value })}
                  placeholder="e.g. Family-friendly atmosphere"
                  className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-white/55">Type</span>
                <select
                  value={editing.category || 'food'}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
                >
                  {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <p className="text-[11px] text-white/50">Reorder using the ↑ ↓ controls on the main page.</p>
              <div className="flex gap-3">
                <button onClick={addImage} className="flex-1 h-11 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold">Save</button>
                <button onClick={() => setEditing(null)} className="h-11 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Taste caption editor for linked menu items */}
      {captionEdit && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setCaptionEdit(null)}>
          <div
            className="w-full max-w-md sb-card p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg text-white">EDIT TASTE CAPTION</div>
              <button onClick={() => setCaptionEdit(null)} className="text-white/70"><X size={18} /></button>
            </div>
            <div className="text-[11px] uppercase tracking-widest text-white/55 mb-1">Dish</div>
            <div className="text-white mb-3">{captionEdit.menu_item_name}</div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-white/55">Caption</span>
              <input
                autoFocus
                value={captionEdit.caption || ''}
                onChange={(e) => setCaptionEdit({ ...captionEdit, caption: e.target.value })}
                className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
              />
            </label>
            <div className="flex gap-2 mt-4">
              <button onClick={saveCaption} className="flex-1 h-10 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold">Save caption</button>
              <button onClick={() => setCaptionEdit(null)} className="h-10 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
